import React, { useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip as Dica,
} from '@chakra-ui/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  CaretDown,
  CaretUp,
  CheckCircle,
  CurrencyCircleDollar,
  FileText,
  Hourglass,
  Info,
  PaperPlaneTilt,
  Robot,
} from 'phosphor-react';
import { GlassCard } from '../../../components/glass';
import {
  IEventoListItem,
  IPainelContratos,
  IDiaRobo,
  IPainelEventos,
  IPainelRobo,
  useEventos,
} from '../../../hooks/useEventos';
import { IDataNoPagination } from '../../../types/shared';
import { formatCurrency } from '../../../utils/FormatFunctions';
// A anatomia do cartão segue a tela da Conciliação Contábil (pedido do user, 21/10): mesma mini-linha e mesma
// regra de cor da variação.
import {
  Sparkline,
  deltaColorFor,
} from '../../modulesHome/accountingConciliation/conciliacao-contabil/components/KPICards';

// Cor de cada cartão (a barra no topo, o ícone e a mini-linha). O estado do evento usa as cores de situação:
// verde em dia, amarelo aguardando assinatura, vermelho parado.
const COR = {
  abertos: '#2a78d6',
  robo: '#6366F1',
  assinados: '#10B981',
  aguardando: '#D9A441',
  valor: '#8B5CF6',
  envio: '#1B88F5',
  neutra: '#a0aec0',
  trilho: '#edf2f7',
  emDia: '#10B981',
  pendente: '#D9A441',
  parado: '#E0524D',
};

const NUM = { fontVariantNumeric: 'tabular-nums' };

// O funil de um contrato, na ordem; cancelado só aparece quando existe.
const ETAPAS_CONTRATO: [string, string][] = [
  ['proposta', 'Proposta'],
  ['contrato', 'Contrato'],
  ['enviado_assinatura', 'Enviado para assinatura'],
  ['assinado', 'Assinado'],
  ['aguardando_nf', 'Aguardando NF'],
  ['nf_emitida', 'NF emitida'],
  ['pago', 'Pago'],
  ['cancelado', 'Cancelado'],
];

const PERIODOS = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
  { dias: 0, rotulo: 'Tudo' },
];

// Datas do monitoramento chegam em horário de São Paulo, sem fuso: lidas literalmente, sem `Date`.
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const quando = (iso?: string | null) => (iso ? `${ddmm(iso)} às ${iso.slice(11, 16)}` : '—');

const contagem = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

// Só para medir "há quanto tempo": o texto do monitoramento é horário de São Paulo, que não tem horário de
// verão desde 2019, então -03:00 fixo vale. Para exibir data e hora, continua a leitura literal acima.
const instante = (iso: string) => Date.parse(`${iso.slice(0, 19)}-03:00`);

const haQuanto = (ms: number) => {
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return 'agora há pouco';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `há ${contagem(h, 'hora', 'horas')}`;
  return `há ${contagem(Math.round(h / 24), 'dia', 'dias')}`;
};

// Tempo em linguagem de gente, arredondado antes de escolher a unidade: 4 min, 3 h 20 min,
// 1 dia e 2 horas, 3 dias.
const tempo = (s: number | null) => {
  if (s == null) return '—';
  const min = Math.max(1, Math.round(s / 60));
  if (min < 60) return `${min} min`;
  if (min < 24 * 60)
    return min % 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min / 60} h`;
  const h = Math.round(min / 60);
  const dias = contagem(Math.floor(h / 24), 'dia', 'dias');
  return h % 24 ? `${dias} e ${contagem(h % 24, 'hora', 'horas')}` : dias;
};

// Duração de rodada: segundos só abaixo de 1 minuto (já arredondado), o resto como `tempo`.
const duracao = (s?: number | null) => {
  if (s == null) return '—';
  return Math.round(s) < 60 ? `${Math.round(s)} s` : tempo(s);
};

const num = (v: number) => v.toLocaleString('pt-BR');

const pct = (parte: number, todo: number) => (todo ? Math.round((parte / todo) * 100) : 0);

// Valor curto para caber no cartão: R$ 1,51 mi, R$ 15,8 mil; abaixo de mil, o valor inteiro.
const moeda = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`;
  if (a >= 1e3) return `R$ ${(v / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return formatCurrency(v);
};

// Segunda-feira da semana de `d`, no calendário de quem olha a tela, como 'AAAA-MM-DD'.
const segundaDe = (d: Date) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${dois(x.getMonth() + 1)}-${dois(x.getDate())}`;
};

// A API pula semana sem contrato: completa com zero do início do período (em "Tudo", da 1ª semana com contrato)
// até a semana atual, para semana parada não sumir da linha. A semana atual e a do corte do período são
// incompletas. São datas de calendário (sem hora), então o passo em UTC não depende de fuso.
function semanasCompletas(semanas: IPainelContratos['por_semana'], dias: number) {
  if (!semanas.length) return [];
  const mapa = new Map(semanas.map(s => [s.semana, s] as const));
  const hoje = new Date();
  const atual = segundaDe(hoje);
  const corte = dias
    ? segundaDe(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - dias))
    : null;
  const ini = corte && corte < semanas[0].semana ? corte : semanas[0].semana;
  const ult = semanas[semanas.length - 1].semana;
  const fim = Date.parse(`${ult > atual ? ult : atual}T00:00:00Z`);
  const linhas: {
    automatico: number;
    total: number;
    assinados: number;
    valor: number;
    parcial: boolean;
  }[] = [];
  for (let t = Date.parse(`${ini}T00:00:00Z`); t <= fim; t += 7 * 86400000) {
    const chave = new Date(t).toISOString().slice(0, 10);
    const s = mapa.get(chave);
    linhas.push({
      automatico: s?.robo ?? 0,
      total: (s?.robo ?? 0) + (s?.manual ?? 0),
      assinados: s?.assinados ?? 0,
      valor: s?.valor ?? 0,
      parcial: chave === atual || chave === corte,
    });
  }
  return linhas;
}

// Variação da última semana completa sobre a anterior: a semana em curso, ainda pela metade, pareceria queda.
const variacao = (serie: number[]) =>
  serie.length >= 2 ? serie[serie.length - 1] - serie[serie.length - 2] : null;

// A mini-linha mostra as últimas 12 semanas: em "Tudo", o histórico inteiro viraria um risco.
const ULTIMAS = 12;

const SETA =
  'A seta compara a última semana completa com a anterior; verde quando vai no bom sentido.';

// Definição de um número, no ícone "i" ao lado do título: mouse ou teclado.
function Ajuda({ texto }: { texto: string }) {
  return (
    <Dica label={texto} hasArrow placement="top" maxW="320px" fontSize="12px" lineHeight="1.5">
      <Box
        as="span"
        display="inline-flex"
        color="gray.400"
        cursor="help"
        tabIndex={0}
        aria-label={texto}
        borderRadius="full"
        _hover={{ color: 'gray.600' }}
        _focusVisible={{ outline: '2px solid', outlineColor: 'blue.300' }}
      >
        <Info size={13} weight="bold" />
      </Box>
    </Dica>
  );
}

function Ponto({ cor }: { cor: string }) {
  return <Box w="8px" h="8px" borderRadius="full" bg={cor} flexShrink={0} />;
}

// Ponto no meio da primeira linha do texto ao lado, mesmo quando o texto quebra.
function ComPonto({ cor, children }: { cor: string; children: React.ReactNode }) {
  return (
    <HStack spacing={2.5} align="flex-start" lineHeight="20px">
      <Flex h="20px" align="center" flexShrink={0}>
        <Ponto cor={cor} />
      </Flex>
      <Box minW={0}>{children}</Box>
    </HStack>
  );
}

// O cartão da Conciliação Contábil: barra de cor no topo, ícone, rótulo e "i"; o número com a mini-linha das
// semanas; embaixo, a variação (verde no bom sentido) e uma linha de contexto.
function Cartao({
  rotulo,
  icone,
  cor,
  valor,
  ajuda,
  serie,
  delta,
  deltaTexto,
  caiEBom,
  contexto,
}: {
  rotulo: string;
  icone: React.ReactNode;
  cor: string;
  valor: string;
  ajuda: string;
  serie?: number[];
  delta?: number | null;
  deltaTexto?: string;
  caiEBom?: boolean;
  contexto: string;
}) {
  const corDelta = deltaColorFor(delta ?? null, caiEBom);
  return (
    <GlassCard intensity="strong" p={0} h="full" overflow="hidden">
      <Box h="3px" w="full" bg={cor} />
      <Flex direction="column" gap={2} h="full" p={4}>
        <Flex align="center" gap={1.5}>
          <Box color={cor} display="flex" aria-hidden>
            {icone}
          </Box>
          <Text fontSize="12px" fontWeight="600" color="gray.600" flex={1} noOfLines={2}>
            {rotulo}
          </Text>
          <Ajuda texto={ajuda} />
        </Flex>
        {/* o valor longo (R$ 281,1 mil) empurra a mini-linha para baixo em vez de sair do cartao */}
        <Flex align="flex-end" justify="space-between" gap={1} wrap="wrap">
          <Text
            fontSize={{ base: '22px', md: '26px' }}
            fontWeight="700"
            color="gray.900"
            letterSpacing="-0.02em"
            lineHeight="1.05"
            whiteSpace="nowrap"
            sx={NUM}
          >
            {valor}
          </Text>
          {serie && serie.length > 1 && (
            <Box flexShrink={0} pb={1} aria-hidden>
              <Sparkline data={serie} color={cor} />
            </Box>
          )}
        </Flex>
        <Flex align="center" gap={2} mt="auto" wrap="wrap">
          {delta != null && delta !== 0 && (
            <Flex
              align="center"
              gap={0.5}
              px={1.5}
              py={0.5}
              borderRadius="md"
              bg={`${corDelta}14`}
              color={corDelta}
            >
              {delta < 0 ? (
                <CaretDown size={10} weight="bold" />
              ) : (
                <CaretUp size={10} weight="bold" />
              )}
              <Text fontSize="10.5px" fontWeight="700">
                {deltaTexto ?? num(Math.abs(delta))}
              </Text>
            </Flex>
          )}
          <Text fontSize="11px" color="gray.500" noOfLines={1}>
            {contexto}
          </Text>
        </Flex>
      </Flex>
    </GlassCard>
  );
}

function Cartoes({ c, dias }: { c: IPainelContratos; dias: number }) {
  const semanas = semanasCompletas(c.por_semana, dias).slice(-ULTIMAS);
  const fechadas = semanas.filter(s => !s.parcial);
  const comContrato = semanas.filter(s => s.total);
  const fechadasComContrato = fechadas.filter(s => s.total);
  const pctRobo = (s: { automatico: number; total: number }) => pct(s.automatico, s.total);
  const deltaRobo = variacao(fechadasComContrato.map(pctRobo));
  const deltaValor = variacao(fechadas.map(s => s.valor));
  const envio = c.tempos.enviados;
  return (
    // seis lado a lado so' em tela larga: abaixo disso o valor e o contexto nao cabem
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, '2xl': 6 }} spacing={4}>
      <Cartao
        rotulo="Contratos abertos"
        icone={<FileText size={18} weight="duotone" />}
        cor={COR.abertos}
        valor={num(c.total)}
        serie={semanas.map(s => s.total)}
        delta={variacao(fechadas.map(s => s.total))}
        contexto={`${num(c.robo)} pelo robô`}
        ajuda={`Contratos criados no período, por quem abriu o card do contrato. ${SETA}`}
      />
      <Cartao
        rotulo="Feitos pelo robô"
        icone={<Robot size={18} weight="duotone" />}
        cor={COR.robo}
        valor={`${pct(c.robo, c.total)}%`}
        serie={comContrato.map(pctRobo)}
        delta={deltaRobo}
        deltaTexto={deltaRobo == null ? undefined : `${Math.abs(deltaRobo)} p.p.`}
        contexto={`${num(c.robo)} de ${num(c.total)} contratos`}
        ajuda={`Parte dos contratos do período abertos pelo robô de contratos. ${SETA}`}
      />
      <Cartao
        rotulo="Assinados"
        icone={<CheckCircle size={18} weight="duotone" />}
        cor={COR.assinados}
        valor={num(c.assinados)}
        serie={semanas.map(s => s.assinados)}
        delta={variacao(fechadas.map(s => s.assinados))}
        contexto={`${pct(c.assinados, c.total)}% dos abertos`}
        ajuda={`Contratos do período que já foram assinados (estão em assinado, aguardando NF, NF emitida ou pago). A linha e a seta contam pela semana em que o contrato foi aberto. ${SETA}`}
      />
      <Cartao
        rotulo="Aguardando assinatura"
        icone={<Hourglass size={18} weight="duotone" />}
        cor={c.parados ? COR.parado : COR.aguardando}
        valor={num(c.aguardando)}
        caiEBom
        contexto={
          c.parados
            ? `${num(c.parados)} enviados há mais de 7 dias`
            : 'nenhum enviado há mais de 7 dias'
        }
        ajuda="Contratos que estão agora em “enviado para assinatura”, de qualquer período: é a situação de hoje. Os enviados há mais de 7 dias, pela data no histórico do contrato, são os que pedem cobrança."
      />
      <Cartao
        rotulo="Valor contratado"
        icone={<CurrencyCircleDollar size={18} weight="duotone" />}
        cor={COR.valor}
        valor={moeda(c.valor_total)}
        serie={semanas.map(s => s.valor)}
        delta={deltaValor}
        deltaTexto={deltaValor == null ? undefined : moeda(Math.abs(deltaValor))}
        contexto={
          c.com_valor ? `média de ${moeda(c.valor_total / c.com_valor)}` : 'sem valor informado'
        }
        ajuda={`Soma do valor dos contratos do período, sem os cancelados. A média conta só os contratos com valor. ${SETA}`}
      />
      <Cartao
        rotulo="Tempo até enviar"
        icone={<PaperPlaneTilt size={18} weight="duotone" />}
        cor={COR.envio}
        valor={tempo(envio.robo.mediana_s)}
        contexto={`pelo robô; pela equipe, ${tempo(envio.pessoas.mediana_s)}`}
        ajuda="Mediana do tempo entre abrir o contrato e o primeiro envio para assinatura: metade dos contratos foi enviada em até esse tempo. O número grande é o do robô; o da equipe vem embaixo."
      />
    </SimpleGrid>
  );
}

type EventoPainel = IPainelContratos['por_evento'][number];

const estadoDo = (e: EventoPainel) => {
  if (e.parados) return { cor: COR.parado, nome: 'Parado há mais de 7 dias' };
  if (e.aguardando) return { cor: COR.pendente, nome: 'Aguardando assinatura' };
  return { cor: COR.emDia, nome: 'Em dia' };
};

// Um evento da grade: estado (ponto), contratos, assinados e a barra do que já foi assinado.
function BlocoEvento({
  e,
  ativo,
  onClick,
}: {
  e: EventoPainel;
  ativo: boolean;
  onClick: () => void;
}) {
  const estado = estadoDo(e);
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      w="full"
      textAlign="left"
      p={3}
      borderRadius="14px"
      bg={ativo ? 'rgba(42,120,214,0.06)' : 'rgba(15,23,42,0.02)'}
      borderWidth="1.5px"
      borderColor={ativo ? COR.abertos : 'transparent'}
      transition="all 0.15s"
      _hover={{ bg: 'rgba(42,120,214,0.05)' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'blue.300' }}
    >
      <Flex align="flex-start" justify="space-between" gap={2} mb={1.5}>
        <Text fontSize="12px" fontWeight="600" color="gray.700" noOfLines={2}>
          {e.evento_nome}
        </Text>
        <Box mt="4px" title={estado.nome}>
          <Ponto cor={estado.cor} />
        </Box>
      </Flex>
      <Text fontSize="22px" fontWeight="700" color="gray.900" lineHeight="1" sx={NUM}>
        {num(e.total)}
      </Text>
      <Text fontSize="11px" color="gray.500" mt={0.5} mb={2}>
        {e.total
          ? `${contagem(e.total, 'contrato', 'contratos')} · ${num(e.assinados)} assinados`
          : 'nenhum no período; aguardando assinatura de antes'}
      </Text>
      <Box h="5px" borderRadius="full" bg={COR.trilho} overflow="hidden" aria-hidden>
        {/* cancelado nunca vai ser assinado: fica fora da barra */}
        <Box
          h="100%"
          w={`${pct(e.assinados, e.total - (e.por_status.cancelado ?? 0))}%`}
          bg={COR.assinados}
          borderRadius="full"
        />
      </Box>
    </Box>
  );
}

// O evento escolhido na grade: onde estão os contratos dele, quanto saiu do robô, valor e o que espera assinatura.
function DetalheEvento({ e }: { e: EventoPainel }) {
  const etapas = ETAPAS_CONTRATO.map(([chave, nome]) => ({
    chave,
    nome,
    n: e.por_status[chave] ?? 0,
  })).filter(x => x.chave !== 'cancelado' || x.n);
  const maior = Math.max(1, ...etapas.map(x => x.n));
  const estado = estadoDo(e);
  return (
    <GlassCard intensity="strong" p={5} h="full">
      <Flex direction="column" gap={4} h="full">
        <Box>
          <Text fontSize="15px" fontWeight="700" color="gray.900" noOfLines={2}>
            {e.evento_nome}
          </Text>
          <HStack spacing={2} mt={1} fontSize="12px" color="gray.500">
            <Ponto cor={estado.cor} />
            <Text>{estado.nome}</Text>
          </HStack>
        </Box>
        {/* uma grade so': a coluna do nome tem a largura do maior, e as barras comecam juntas */}
        <Grid
          as="dl"
          templateColumns="auto minmax(48px, 1fr) 36px"
          alignItems="center"
          columnGap={3}
          rowGap={2.5}
        >
          {etapas.map(x => (
            <React.Fragment key={x.chave}>
              <Text as="dt" fontSize="12.5px" color="gray.600" noOfLines={1}>
                {x.nome}
              </Text>
              <Box h="6px" borderRadius="full" bg={COR.trilho} overflow="hidden" aria-hidden>
                <Box h="100%" w={`${pct(x.n, maior)}%`} bg={COR.neutra} borderRadius="full" />
              </Box>
              <Text as="dd" fontSize="12.5px" color="gray.800" textAlign="right" sx={NUM}>
                {num(x.n)}
              </Text>
            </React.Fragment>
          ))}
        </Grid>
        <SimpleGrid columns={2} spacing={3} pt={3} borderTop="1px solid" borderColor="gray.100">
          <Box>
            <Text fontSize="11px" color="gray.500">
              Pelo robô
            </Text>
            <Text fontSize="16px" fontWeight="700" color="gray.900" sx={NUM}>
              {`${pct(e.robo, e.total)}%`}
            </Text>
          </Box>
          <Box>
            <Text fontSize="11px" color="gray.500">
              Valor
            </Text>
            <Text fontSize="16px" fontWeight="700" color="gray.900" sx={NUM}>
              {moeda(e.valor)}
            </Text>
          </Box>
          <Box gridColumn="span 2">
            <Text fontSize="11px" color="gray.500">
              Aguardando assinatura hoje (de qualquer período)
            </Text>
            <Text fontSize="13px" color="gray.800">
              {e.aguardando
                ? `${num(e.aguardando)}${e.parados ? `, ${num(e.parados)} há mais de 7 dias` : ''}`
                : 'nenhum'}
            </Text>
          </Box>
        </SimpleGrid>
        <Button
          as={RouterLink}
          to={`/eventos?evento=${e.evento_id}`}
          size="sm"
          variant="outline"
          borderRadius="full"
          mt="auto"
          alignSelf="flex-start"
        >
          Abrir evento
        </Button>
      </Flex>
    </GlassCard>
  );
}

function Eventos({ c }: { c: IPainelContratos }) {
  const [escolhido, setEscolhido] = useState<number | null>(null);
  const eventos = c.por_evento;
  if (!eventos.length) {
    return (
      <GlassCard intensity="strong" p={5}>
        <Text fontSize="14px" color="gray.600">
          Nenhum contrato aberto neste período.
        </Text>
      </GlassCard>
    );
  }
  // o escolhido some quando troca o filtro: volta para o evento com mais contratos
  const atual = eventos.find(e => e.evento_id === escolhido) ?? eventos[0];
  return (
    // lado a lado so' a partir de 1280 px: abaixo disso o detalhe nao tem largura para as barras
    <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={5} alignItems="start">
      <GlassCard intensity="strong" p={{ base: 4, md: 5 }}>
        <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={3}>
          {eventos.map(e => (
            <BlocoEvento
              key={e.evento_id}
              e={e}
              ativo={e.evento_id === atual.evento_id}
              onClick={() => setEscolhido(e.evento_id)}
            />
          ))}
        </SimpleGrid>
        <Flex
          wrap="wrap"
          gap={4}
          justify="center"
          mt={5}
          pt={4}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          {[
            [COR.emDia, 'Em dia'],
            [COR.pendente, 'Aguardando assinatura'],
            [COR.parado, 'Parado há mais de 7 dias'],
          ].map(([cor, nome]) => (
            <HStack key={nome} spacing={2}>
              <Ponto cor={cor} />
              <Text fontSize="11.5px" color="gray.600">
                {nome}
              </Text>
            </HStack>
          ))}
        </Flex>
      </GlassCard>
      <DetalheEvento e={atual} />
    </Grid>
  );
}

// Até quanto o robô costuma ficar sem rodar, pelos dias mais recentes até somar 21 rodadas (o monitoramento
// soma as rodadas por dia): o dobro do intervalo médio entre rodadas dentro do dia e a maior pausa de um dia de
// rodada ao seguinte (a noite, às vezes o fim de semana), nunca menos que 30 min. A agenda da VPS não chega ao
// painel, então o aviso é conservador: só aparece depois de passar da maior pausa.
function folgaNormal(dias: IDiaRobo[]) {
  const recentes: IDiaRobo[] = [];
  for (let i = dias.length - 1, n = 0; i >= 0 && n < 21; i -= 1) {
    recentes.unshift(dias[i]);
    n += dias[i].rodadas;
  }
  const dentro = recentes
    .filter(d => d.rodadas > 1)
    .map(d => (instante(d.fim) - instante(d.inicio)) / (d.rodadas - 1))
    .sort((a, b) => a - b);
  const entre = recentes
    .slice(1)
    .map((d, i) => instante(d.inicio) - instante(recentes[i].fim))
    .filter(g => g > 0);
  if (recentes.reduce((t, d) => t + d.rodadas, 0) < 4) return null;
  const dobro = dentro.length ? 2 * dentro[Math.floor(dentro.length / 2)] : 0;
  return Math.max(dobro, ...entre, 30 * 60000);
}

// A situação do robô: rodando agora, ou quando terminou a última rodada; aviso quando está parado há mais que a
// pausa normal dele.
function Situacao({ robo }: { robo: IPainelRobo }) {
  const andando = robo.em_andamento?.[0];
  const dias = robo.dias ?? [];
  const ultimo = dias[dias.length - 1];
  const folga = folgaNormal(dias);
  const desde = ultimo ? Date.now() - instante(ultimo.fim) : 0;
  const atrasado = !andando && folga != null && desde > folga;
  let frase = 'Sem rodadas no período.';
  if (andando) {
    frase = `Rodando agora: ${num(andando.done)} de ${andando.total == null ? '?' : num(andando.total)} cards.`;
  } else if (ultimo) {
    frase = `Última rodada terminou ${haQuanto(desde)} (${quando(ultimo.fim)}).`;
  }
  return (
    <Flex direction="column" gap={2}>
      <ComPonto cor={andando ? COR.abertos : COR.neutra}>
        <Text fontSize="13.5px" color="gray.700">
          {frase}
        </Text>
      </ComPonto>
      {atrasado && (
        <Alert status="warning" borderRadius="8px" py={2} fontSize="13px">
          <AlertIcon boxSize="16px" />
          {`O robô não roda ${haQuanto(desde)}, mais que a maior pausa dos últimos dias. `}
          Vale conferir o servidor.
        </Alert>
      )}
    </Flex>
  );
}

// A faixa do robô: situação, três números e a mini-linha das rodadas por dia.
function FaixaRobo({
  robo,
  carregando,
  onTentar,
}: {
  robo?: IPainelRobo;
  carregando: boolean;
  onTentar: () => void;
}) {
  if (!robo && carregando) {
    return (
      <GlassCard intensity="strong" p={5}>
        <HStack spacing={3}>
          <Spinner size="sm" color="gray.400" />
          <Text fontSize="13.5px" color="gray.500">
            Buscando as rodadas no monitoramento…
          </Text>
        </HStack>
      </GlassCard>
    );
  }
  if (!robo) {
    return (
      <Alert status="warning" borderRadius="12px" flexWrap="wrap" gap={2}>
        <AlertIcon />
        <Text flex="1 1 280px">
          O monitoramento não respondeu e os dados do robô não carregaram. Os números de contratos
          acima seguem valendo.
        </Text>
        <Button size="sm" variant="outline" bg="white" onClick={onTentar}>
          Tentar de novo
        </Button>
      </Alert>
    );
  }
  const dias = robo.dias ?? [];
  if (!dias.length && !robo.em_andamento?.length) {
    return (
      <GlassCard intensity="strong" p={5}>
        <Text fontSize="14px" color="gray.700">
          Nenhuma rodada do robô neste período.
        </Text>
        <Text fontSize="13px" color="gray.500" mt={1}>
          Se ele parou há mais tempo, escolha um período maior (o monitoramento entrega até 62
          dias).
        </Text>
      </GlassCard>
    );
  }
  const rodadas = dias.reduce((t, d) => t + d.rodadas, 0);
  const segundos = dias.reduce((t, d) => t + d.segundos, 0);
  const numeros = [
    { rotulo: 'Rodadas', valor: num(rodadas) },
    { rotulo: 'Dias com rodada', valor: num(dias.length) },
    { rotulo: 'Duração média', valor: rodadas ? duracao(segundos / rodadas) : '—' },
  ];
  return (
    <GlassCard intensity="strong" p={5}>
      <Flex gap={6} wrap="wrap" align="center" justify="space-between">
        <Box flex="1 1 280px">
          <Situacao robo={robo} />
        </Box>
        <HStack spacing={0} flex="2 1 420px" align="stretch">
          {numeros.map((n, i) => (
            <Box
              key={n.rotulo}
              flex={1}
              pl={i ? 4 : 0}
              borderLeft={i ? '1px solid' : 'none'}
              borderColor="gray.100"
            >
              <Text fontSize="12px" color="gray.500">
                {n.rotulo}
              </Text>
              <Text fontSize="20px" fontWeight="700" color="gray.900" lineHeight="1.2" sx={NUM}>
                {n.valor}
              </Text>
            </Box>
          ))}
          {dias.length > 1 && (
            <Box pl={4} borderLeft="1px solid" borderColor="gray.100">
              <Text fontSize="12px" color="gray.500" mb={1}>
                Rodadas por dia
              </Text>
              <Box aria-hidden>
                <Sparkline data={dias.slice(-30).map(d => d.rodadas)} color={COR.abertos} />
              </Box>
            </Box>
          )}
        </HStack>
      </Flex>
      {robo.limitado && robo.desde && (
        <Text fontSize="12px" color="gray.400" mt={3}>
          {`Rodadas desde ${ddmm(robo.desde)}: o monitoramento entrega no máximo os últimos 62 dias.`}
        </Text>
      )}
    </GlassCard>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box as="section" aria-label={titulo}>
      <Text as="h2" fontSize="16px" fontWeight="700" color="gray.900" mb={3}>
        {titulo}
      </Text>
      {children}
    </Box>
  );
}

/**
 * Painel Executivo de Eventos, visão macro para a diretoria (21/10), no molde da tela da Conciliação Contábil:
 * seis cartões do período, a grade de eventos com o detalhe do escolhido e a faixa do robô de contratos.
 */
export default function PainelExecutivo() {
  const { getPainel, getPainelRobo, getEventos } = useEventos();
  const [dias, setDias] = useState(30);
  const [eventoId, setEventoId] = useState('');

  const { data: eventos } = useQuery<IDataNoPagination<IEventoListItem[]>>({
    queryKey: ['eventos', {}],
    queryFn: getEventos,
  });
  // Ao trocar o filtro, o painel anterior fica na tela (esmaecido) até o novo chegar, sem salto de
  // layout. Refetch em segundo plano não esmaece nada.
  const { data, isFetching, isError, error, isPlaceholderData } = useQuery<
    IDataNoPagination<IPainelEventos>
  >({
    queryKey: ['eventos-painel', { dias, evento_id: eventoId || undefined, robo: false }],
    queryFn: getPainel,
    placeholderData: keepPreviousData,
  });
  const painel = data?.data;
  // Servidor ainda sem o painel novo (admin publicado antes do back): sem `aguardando`, os cartões quebrariam.
  const servidorAntigo = !!painel && painel.contratos.aguardando === undefined;
  const semAcesso = (error as any)?.response?.status === 403;
  // O robô vem à parte: não depende do evento, e o monitoramento lento segura só esta faixa (prod, 21/10).
  const robo = useQuery<IDataNoPagination<IPainelRobo>>({
    queryKey: ['eventos-painel-robo', { dias }],
    queryFn: getPainelRobo,
    placeholderData: keepPreviousData,
  });
  // `dias` do painel que está na tela: na troca de filtro o anterior fica até o novo chegar
  const diasDoPainel = useRef(dias);
  if (painel && !isPlaceholderData) diasDoPainel.current = dias;
  // o painel só conta eventos ativos e concluídos (como o Financeiro); o já escolhido fica mesmo se for
  // arquivado com a tela aberta, senão o select mostraria "Todos" com o painel ainda filtrado
  const opcoes = (eventos?.data ?? []).filter(
    e => e.status === 'ativo' || e.status === 'concluido' || String(e.id) === eventoId,
  );
  const nomeEvento = opcoes.find(e => String(e.id) === eventoId)?.nome;
  // O recorte aplicado fica escrito: com dado só recente, 30 dias e "tudo" podem dar o mesmo número.
  const recorte = `${dias ? `Últimos ${dias} dias` : 'Todo o histórico'}, ${
    nomeEvento ? `no evento ${nomeEvento}` : 'em todos os eventos'
  }.`;

  return (
    <Box>
      <Flex gap={3} mb={2} wrap="wrap" align="center">
        <ButtonGroup size="sm" isAttached variant="outline" role="radiogroup" aria-label="Período">
          {PERIODOS.map(p => {
            const ativo = p.dias === dias;
            return (
              <Button
                key={p.dias}
                role="radio"
                aria-checked={ativo}
                onClick={() => setDias(p.dias)}
                bg={ativo ? 'gray.900' : 'white'}
                color={ativo ? 'white' : 'gray.600'}
                borderColor="gray.200"
                fontWeight="500"
                _hover={{ bg: ativo ? 'gray.900' : 'gray.50' }}
              >
                {p.rotulo}
              </Button>
            );
          })}
        </ButtonGroup>
        <Select
          aria-label="Evento"
          size="sm"
          w={{ base: '100%', md: '300px' }}
          bg="white"
          borderRadius="8px"
          borderColor="gray.200"
          value={eventoId}
          onChange={e => setEventoId(e.target.value)}
        >
          <option value="">Todos os eventos</option>
          {opcoes.map(e => (
            <option key={e.id} value={e.id}>
              {e.dt_evento ? `${e.nome} (${e.dt_evento.split('-').reverse().join('/')})` : e.nome}
            </option>
          ))}
        </Select>
        {isFetching && <Spinner size="sm" color="gray.400" />}
      </Flex>
      <Text fontSize="13px" color="gray.500" mb={6}>
        {recorte}
      </Text>

      {isError && !isFetching && (
        <Alert status={semAcesso ? 'info' : 'error'} borderRadius="12px" mb={6}>
          <AlertIcon />
          {semAcesso
            ? 'Você não tem acesso ao painel executivo. Peça a permissão a um administrador.'
            : 'O painel não carregou. Tente de novo em instantes; se continuar, avise a TI.'}
        </Alert>
      )}

      {servidorAntigo && (
        <Alert status="info" borderRadius="12px" mb={6}>
          <AlertIcon />O painel novo ainda está sendo publicado no servidor. Tente de novo em alguns
          minutos.
        </Alert>
      )}

      {!painel && isFetching && (
        <Flex justify="center" py={16}>
          <Spinner size="lg" color="gray.300" thickness="3px" />
        </Flex>
      )}

      {painel && !servidorAntigo && (
        <Flex
          direction="column"
          gap={8}
          opacity={isPlaceholderData ? 0.55 : 1}
          transition="opacity 0.2s"
          aria-busy={isPlaceholderData}
        >
          <Secao titulo="Contratos">
            <Cartoes c={painel.contratos} dias={diasDoPainel.current} />
          </Secao>
          <Secao titulo="Eventos">
            <Eventos c={painel.contratos} />
          </Secao>
          <Secao titulo="Robô de contratos">
            {nomeEvento && (
              <Text fontSize="13px" color="gray.500" mt={-1} mb={3}>
                A rodada do robô não é separada por evento: os números abaixo valem para todos.
              </Text>
            )}
            {/* troca de período: as rodadas do período anterior ficam esmaecidas até as novas chegarem */}
            <Box
              opacity={robo.isPlaceholderData ? 0.55 : 1}
              transition="opacity 0.2s"
              aria-busy={robo.isPlaceholderData}
            >
              <FaixaRobo
                robo={robo.data?.data}
                carregando={robo.isFetching}
                onTentar={() => robo.refetch()}
              />
            </Box>
          </Secao>
        </Flex>
      )}
    </Box>
  );
}
